import { IconButton } from "@hortis/ui/button";
import { Dot } from "@hortis/ui/dot";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@hortis/ui/hover-card";
import {
  ArrowCircleBrokenDownRight,
  BookOpen1,
  CloudSun1,
  CryptoCurrency2,
  Edit2,
  ExternalLink1,
  Globe3,
  Tag3,
} from "@hortis/ui/icons";
import {
  redListStatusClassNames,
  redListStatusLabelMap,
} from "@hortis/ui/iucn-red-list-tag";
import { Tag } from "@hortis/ui/tag";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { Link } from "@tanstack/react-router";
import { TaxonFieldsFragment } from "generated/graphql";
import {
  MobileProperty,
  RecordProperties,
  RecordPropertiesGroup,
  RecordPropertiesGroupTitle,
  RecordPropertiesItem,
  RecordPropertiesItemLabel,
  RecordPropertiesItemValue,
  ViewProperty,
} from "src/components/record-properties/record-properties";
import { twMerge } from "tailwind-merge";

export const TaxonViewProperties = ({
  taxon,
  className,
}: { taxon: TaxonFieldsFragment; className?: string }) => {
  const tags = taxon.tagsConnection?.nodes ?? [];
  return (
    <div className={twMerge("flex flex-wrap gap-x-3 gap-y-1.5", className)}>
      {(taxon.authorship != null ||
        (taxon.sharedScientificName?.authorship != null &&
          taxon.fullyValidated)) && (
        <ViewProperty>
          <BookOpen1 className="h-4 w-4" />
          {taxon.authorship ?? taxon.sharedScientificName?.authorship}
        </ViewProperty>
      )}
      {(taxon.nativeDistribution != null ||
        (taxon.sharedScientificName?.taxon?.nativeDistribution != null &&
          taxon.fullyValidated)) && (
        <ViewProperty>
          <Globe3 className="h-4 w-4" />
          {taxon.nativeDistribution ??
            taxon.sharedScientificName?.taxon?.nativeDistribution}
        </ViewProperty>
      )}
      {taxon.sharedScientificName?.taxon?.globalRedListStatus != null &&
        taxon.fullyValidated && (
          <ViewProperty>
            <Dot
              size="md"
              className={twMerge(
                "rounded-xs",
                redListStatusClassNames[
                  taxon.sharedScientificName.taxon.globalRedListStatus
                ],
              )}
            />
            {
              redListStatusLabelMap[
                taxon.sharedScientificName.taxon.globalRedListStatus
              ]
            }
          </ViewProperty>
        )}
      {taxon.fullyValidated &&
        taxon.sharedScientificName?.taxon?.lifeForms?.nodes != null &&
        taxon.sharedScientificName?.taxon.lifeForms.nodes.length > 0 && (
          <ViewProperty>
            <CryptoCurrency2 className="h-4 w-4" />
            {taxon.sharedScientificName?.taxon.lifeForms.nodes
              .map(({ name }) => name)
              .join(", ")}
          </ViewProperty>
        )}
      {taxon.fullyValidated &&
        taxon.sharedScientificName?.taxon?.climate != null && (
          <ViewProperty>
            <CloudSun1 className="h-4 w-4" />
            {taxon.sharedScientificName?.taxon.climate}
          </ViewProperty>
        )}
      {taxon.fullyValidated &&
        taxon.sharedScientificName?.classification?.family != null && (
          <ViewProperty>
            <CloudSun1 className="h-4 w-4" />
            {taxon.sharedScientificName.classification.family}
          </ViewProperty>
        )}
      {tags.length > 0 && (
        <HoverCard>
          <HoverCardTrigger className="cursor-pointer">
            <ViewProperty>
              <Tag3 className="h-4 w-4" />
              {tags.length > 2
                ? `${tags.length} tags`
                : tags.map(({ name }) => name).join(", ")}
            </ViewProperty>
          </HoverCardTrigger>
          <HoverCardContent className="p-3 space-y-1.5 max-w-xs" align="start">
            <div className="font-medium text-sm text-grey-500">Tags</div>
            <div className="flex flex-wrap items-center gap-1">
              {tags.map(({ id, name }) => (
                <Tag
                  key={id}
                  data-cy="taxon-tag"
                  size="lg"
                  className="px-1.5 py-0.5"
                >
                  {name}
                </Tag>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  );
};

const DerivedDataIcon = () => (
  <Tooltip delayDuration={0}>
    <TooltipTrigger
      className="hover:text-grey-700 transition text-grey-500"
      tabIndex={-1}
    >
      <ArrowCircleBrokenDownRight className="w-3.5 h-3.5" />
    </TooltipTrigger>
    <TooltipContent>Derived from validation</TooltipContent>
  </Tooltip>
);

export const TaxonMobileProperties = ({
  taxon,
  onRequestEdit,
  canEdit,
  className,
}: {
  taxon: NonNullable<TaxonFieldsFragment>;
  onRequestEdit: () => void;
  canEdit: boolean;
  className?: string;
}) => {
  const tags = taxon.tagsConnection?.nodes ?? [];
  return (
    <div className={twMerge("space-y-1 pb-1", className)}>
      <div className="flex flex-wrap gap-1">
        {(taxon.authorship != null ||
          (taxon.sharedScientificName?.authorship != null &&
            taxon.fullyValidated)) && (
          <MobileProperty data-cy="authorship-display">
            <BookOpen1 className="h-4 w-4" />
            {taxon.authorship ?? taxon.sharedScientificName?.authorship}
          </MobileProperty>
        )}
        {(taxon.nativeDistribution != null ||
          (taxon.sharedScientificName?.taxon?.nativeDistribution != null &&
            taxon.fullyValidated)) && (
          <MobileProperty data-cy="native-distribution-display">
            <Globe3 className="h-4 w-4" />
            {taxon.nativeDistribution ??
              taxon.sharedScientificName?.taxon?.nativeDistribution}
          </MobileProperty>
        )}
        {taxon.sharedScientificName?.taxon?.globalRedListStatus != null &&
          taxon.fullyValidated && (
            <MobileProperty data-cy="iucn-red-list-display">
              <Dot
                size="md"
                className={twMerge(
                  "rounded-xs",
                  redListStatusClassNames[
                    taxon.sharedScientificName.taxon.globalRedListStatus
                  ],
                )}
              />
              {
                redListStatusLabelMap[
                  taxon.sharedScientificName.taxon.globalRedListStatus
                ]
              }
            </MobileProperty>
          )}
        {taxon.fullyValidated &&
          taxon.sharedScientificName?.taxon?.lifeForms?.nodes != null &&
          taxon.sharedScientificName?.taxon.lifeForms.nodes.length > 0 && (
            <MobileProperty data-cy="life-forms-display">
              <CryptoCurrency2 className="h-4 w-4" />
              {taxon.sharedScientificName?.taxon.lifeForms.nodes
                .map(({ name }) => name)
                .join(", ")}
            </MobileProperty>
          )}
        {taxon.fullyValidated &&
          taxon.sharedScientificName?.taxon?.climate != null && (
            <MobileProperty data-cy="climate-display">
              <CloudSun1 className="h-4 w-4" />
              {taxon.sharedScientificName?.taxon.climate}
            </MobileProperty>
          )}
        {taxon.fullyValidated &&
          taxon.sharedScientificName?.classification?.family != null && (
            <MobileProperty data-cy="family-display">
              <CloudSun1 className="h-4 w-4" />
              {taxon.sharedScientificName.classification.family}
            </MobileProperty>
          )}
        {tags.length > 0 && (
          <HoverCard>
            <HoverCardTrigger className="cursor-pointer">
              <MobileProperty data-cy="tags-picker">
                <Tag3 className="h-4 w-4" />
                {tags.length > 2
                  ? `${tags.length} tags`
                  : tags.map(({ name }) => name).join(", ")}
              </MobileProperty>
            </HoverCardTrigger>
            <HoverCardContent
              className="p-3 space-y-1.5 max-w-xs"
              align="start"
            >
              <div className="font-medium text-sm text-grey-500">Tags</div>
              <div className="flex flex-wrap items-center gap-1">
                {tags.map(({ id, name }) => (
                  <Tag
                    key={id}
                    data-cy="taxon-tag"
                    size="lg"
                    className="px-1.5 py-0.5"
                  >
                    {name}
                  </Tag>
                ))}
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        <div className="flex items-center gap-1">
          {canEdit && !taxon.archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestEdit}
                  ariaLabel="Edit section"
                  data-cy="properties-edit-button"
                  size="xs"
                  variant="tertiaryGray"
                  className="bg-grey-50 rounded-full p-1.5 hover:bg-grey-100 border border-grey-100"
                  icon={Edit2}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Edit properties</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export const TaxonProperties = ({
  taxon,
  canEdit,
  onRequestEdit,
  className,
}: {
  taxon: NonNullable<TaxonFieldsFragment>;
  onRequestEdit: () => void;
  canEdit: boolean;
  className?: string;
}) => {
  return (
    <RecordProperties className={twMerge("", className)}>
      <RecordPropertiesGroup>
        <RecordPropertiesGroupTitle>
          Properties
          {canEdit && !taxon.archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestEdit}
                  ariaLabel="Edit section"
                  data-cy="properties-edit-button"
                  size="sm"
                  variant="tertiaryGray"
                  icon={Edit2}
                  className="hover:bg-grey-100 absolute -right-2 p-1.5"
                />
              </TooltipTrigger>
              <TooltipContent side="left">Edit taxon</TooltipContent>
            </Tooltip>
          )}
        </RecordPropertiesGroupTitle>
        {(taxon.authorship != null ||
          (taxon.sharedScientificName?.authorship != null &&
            taxon.fullyValidated)) && (
          <RecordPropertiesItem data-cy="authorship-display">
            <RecordPropertiesItemLabel>
              Authorship {taxon.authorship == null && <DerivedDataIcon />}
            </RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {taxon.authorship ?? taxon.sharedScientificName?.authorship}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {(taxon.nativeDistribution != null ||
          (taxon.sharedScientificName?.taxon?.nativeDistribution != null &&
            taxon.fullyValidated)) && (
          <RecordPropertiesItem data-cy="native-distribution-display">
            <RecordPropertiesItemLabel>
              Native distribution
              {taxon.nativeDistribution == null && <DerivedDataIcon />}
            </RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {taxon.nativeDistribution ??
                taxon.sharedScientificName?.taxon?.nativeDistribution}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {taxon.sharedScientificName?.taxon?.globalRedListStatus != null &&
          taxon.fullyValidated && (
            <RecordPropertiesItem data-cy="iucn-red-list-display">
              <RecordPropertiesItemLabel>
                IUCN Red List
                <DerivedDataIcon />
              </RecordPropertiesItemLabel>
              <RecordPropertiesItemValue>
                {
                  redListStatusLabelMap[
                    taxon.sharedScientificName.taxon.globalRedListStatus
                  ]
                }
                <Dot
                  size="md"
                  className={twMerge(
                    "rounded-xs",
                    redListStatusClassNames[
                      taxon.sharedScientificName.taxon.globalRedListStatus
                    ],
                  )}
                />
                {taxon.sharedScientificName.taxon.globalRedListSourceUrl !=
                  null && (
                  <IconButton
                    variant="tertiaryGray"
                    className="p-1!"
                    ariaLabel="IUCN Red List assessment source"
                    testId="red-list-source-link"
                    icon={ExternalLink1}
                    size="xs"
                    asChild
                  >
                    <Link
                      to={
                        taxon.sharedScientificName.taxon.globalRedListSourceUrl
                      }
                      target="_blank"
                    />
                  </IconButton>
                )}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
        {taxon.fullyValidated &&
          taxon.sharedScientificName?.taxon?.lifeForms?.nodes != null &&
          taxon.sharedScientificName?.taxon.lifeForms.nodes.length > 0 && (
            <RecordPropertiesItem data-cy="life-forms-display">
              <RecordPropertiesItemLabel>
                Life forms <DerivedDataIcon />
              </RecordPropertiesItemLabel>
              <RecordPropertiesItemValue>
                {taxon.sharedScientificName?.taxon.lifeForms.nodes
                  .map(({ name }) => name)
                  .join(", ")}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
        {taxon.fullyValidated &&
          taxon.sharedScientificName?.taxon?.climate != null && (
            <RecordPropertiesItem data-cy="climate-display">
              <RecordPropertiesItemLabel>
                Climate <DerivedDataIcon />
              </RecordPropertiesItemLabel>
              <RecordPropertiesItemValue>
                {taxon.sharedScientificName?.taxon.climate}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
        {taxon.fullyValidated &&
          taxon.sharedScientificName?.classification?.family != null && (
            <RecordPropertiesItem data-cy="family-display">
              <RecordPropertiesItemLabel>
                Family <DerivedDataIcon />
              </RecordPropertiesItemLabel>
              <RecordPropertiesItemValue>
                {taxon.sharedScientificName.classification.family}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
        {taxon.tagsConnection?.nodes != null &&
          taxon.tagsConnection.nodes.length > 0 && (
            <RecordPropertiesItem className="gap-2" data-cy="tags-picker">
              <RecordPropertiesItemLabel>Tags</RecordPropertiesItemLabel>
              <RecordPropertiesItemValue className="flex flex-wrap gap-1.5">
                {taxon.tagsConnection.nodes.map(({ id, name }) => (
                  <Tag
                    key={id}
                    data-cy="taxon-tag"
                    size="lg"
                    className="px-1.5 py-0.5"
                  >
                    {name}
                  </Tag>
                ))}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
      </RecordPropertiesGroup>
    </RecordProperties>
  );
};
